/**
 * Material Builder — converts MaterialSlotConfig into Three.js materials.
 * Ported from dice-editor/src/materials/materialBuilder.ts for client runtime.
 */

import * as THREE from 'three';
import type { MaterialSlotConfig } from '@sdl/shared';
import { SHADER_PRESETS, type ShaderPresetDef } from './shaderPresets';

const textureCache = new Map<string, THREE.Texture>();
const textureLoader = new THREE.TextureLoader();

function loadTexture(url: string): THREE.Texture {
  if (textureCache.has(url)) return textureCache.get(url)!;
  const tex = textureLoader.load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = false; // GLB/glTF UVs expect flipY=false
  textureCache.set(url, tex);
  return tex;
}

function getSide(side?: string): THREE.Side {
  if (side === 'back') return THREE.BackSide;
  if (side === 'double') return THREE.DoubleSide;
  return THREE.FrontSide;
}

/** Apply dicelib color boost: 0 → 1.0x, 5 → 2.0x, 10 → 3.0x */
function applyColorBoost(color: THREE.Color, boost: number): THREE.Color {
  if (boost > 0) {
    const multiplier = 1.0 + (boost / 10 * 2.0);
    color.multiplyScalar(multiplier);
  }
  return color;
}

function commonProps(config: MaterialSlotConfig) {
  const color = applyColorBoost(new THREE.Color(config.color ?? '#ffffff'), config.colorBoost ?? 0);
  const props: Record<string, any> = {
    color,
    opacity: config.opacity ?? 1,
    transparent: config.transparent ?? ((config.opacity ?? 1) < 1),
    side: getSide(config.side),
    wireframe: config.wireframe ?? false,
  };

  if (config.map?.url) props.map = loadTexture(config.map.url);
  if (config.alphaMap?.url) props.alphaMap = loadTexture(config.alphaMap.url);

  return props;
}

function buildBasic(config: MaterialSlotConfig): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial(commonProps(config));
}

function buildLambert(config: MaterialSlotConfig): THREE.MeshLambertMaterial {
  const props: any = {
    ...commonProps(config),
    emissive: new THREE.Color(config.emissive ?? '#000000'),
    emissiveIntensity: config.emissiveIntensity ?? 1,
  };
  if (config.emissiveMap?.url) props.emissiveMap = loadTexture(config.emissiveMap.url);
  if (config.aoMap?.url) props.aoMap = loadTexture(config.aoMap.url);
  return new THREE.MeshLambertMaterial(props);
}

function buildPhong(config: MaterialSlotConfig): THREE.MeshPhongMaterial {
  const props: any = {
    ...commonProps(config),
    specular: new THREE.Color(config.specular ?? '#111111'),
    shininess: config.shininess ?? 30,
    emissive: new THREE.Color(config.emissive ?? '#000000'),
    emissiveIntensity: config.emissiveIntensity ?? 1,
    flatShading: config.flatShading ?? false,
  };
  if (config.normalMap?.url) props.normalMap = loadTexture(config.normalMap.url);
  if (config.emissiveMap?.url) props.emissiveMap = loadTexture(config.emissiveMap.url);
  if (config.aoMap?.url) props.aoMap = loadTexture(config.aoMap.url);
  return new THREE.MeshPhongMaterial(props);
}

function buildStandard(config: MaterialSlotConfig): THREE.MeshStandardMaterial {
  const props: any = {
    ...commonProps(config),
    roughness: config.roughness ?? 0.5,
    metalness: config.metalness ?? 0,
    envMapIntensity: config.envMapIntensity ?? 1,
    emissive: new THREE.Color(config.emissive ?? '#000000'),
    emissiveIntensity: config.emissiveIntensity ?? 1,
    flatShading: config.flatShading ?? false,
  };
  if (config.normalMap?.url) props.normalMap = loadTexture(config.normalMap.url);
  if (config.roughnessMap?.url) props.roughnessMap = loadTexture(config.roughnessMap.url);
  if (config.metalnessMap?.url) props.metalnessMap = loadTexture(config.metalnessMap.url);
  if (config.emissiveMap?.url) props.emissiveMap = loadTexture(config.emissiveMap.url);
  if (config.aoMap?.url) props.aoMap = loadTexture(config.aoMap.url);
  return new THREE.MeshStandardMaterial(props);
}

function buildPhysical(config: MaterialSlotConfig): THREE.MeshPhysicalMaterial {
  const props: any = {
    ...commonProps(config),
    roughness: config.roughness ?? 0.5,
    metalness: config.metalness ?? 0,
    envMapIntensity: config.envMapIntensity ?? 1,
    emissive: new THREE.Color(config.emissive ?? '#000000'),
    emissiveIntensity: config.emissiveIntensity ?? 1,
    flatShading: config.flatShading ?? false,
    clearcoat: config.clearcoat ?? 0,
    clearcoatRoughness: config.clearcoatRoughness ?? 0,
    transmission: config.transmission ?? 0,
    thickness: config.thickness ?? 0,
    ior: config.ior ?? 1.5,
    sheen: config.sheen ?? 0,
    sheenRoughness: config.sheenRoughness ?? 0,
    sheenColor: new THREE.Color(config.sheenColor ?? '#ffffff'),
    specularIntensity: config.specularIntensity ?? 1,
    specularColor: new THREE.Color(config.specularColor ?? '#ffffff'),
    iridescence: config.iridescence ?? 0,
    iridescenceIOR: config.iridescenceIOR ?? 1.3,
  };
  if (config.normalMap?.url) props.normalMap = loadTexture(config.normalMap.url);
  if (config.roughnessMap?.url) props.roughnessMap = loadTexture(config.roughnessMap.url);
  if (config.metalnessMap?.url) props.metalnessMap = loadTexture(config.metalnessMap.url);
  if (config.emissiveMap?.url) props.emissiveMap = loadTexture(config.emissiveMap.url);
  if (config.aoMap?.url) props.aoMap = loadTexture(config.aoMap.url);
  return new THREE.MeshPhysicalMaterial(props);
}

/** Shader-preset uniform keys that map onto MeshPhysicalMaterial properties
 *  instead of custom shader uniforms (PBR presets only). */
const PBR_PROP_KEYS = new Set(['tint', 'baseColor', 'roughness', 'metalness', 'clearcoat']);

/**
 * PBR shader preset (e.g. glitter): a MeshPhysicalMaterial patched via
 * onBeforeCompile — the base gets real lighting, shadows, and env reflections;
 * the preset's pbrChunks splice the custom effect into the standard shader.
 * The uniforms object is exposed as `mat.uniforms` (same refs the compiled
 * shader uses, surviving recompiles) so the time driver and the editor's live
 * uniform patching work unchanged.
 */
function buildPbrShader(config: MaterialSlotConfig, presetName: string, preset: ShaderPresetDef): THREE.MeshPhysicalMaterial {
  const raw: Record<string, number | string | number[] | undefined> = {};
  for (const [key, meta] of Object.entries(preset.uniforms)) {
    const stored = config.shaderUniforms?.[key];
    raw[key] = stored !== undefined ? stored.value : meta.default;
  }

  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color((raw.tint ?? raw.baseColor ?? '#ffffff') as string),
    roughness: (raw.roughness as number) ?? 0.5,
    metalness: (raw.metalness as number) ?? 0,
    clearcoat: (raw.clearcoat as number) ?? 0,
    clearcoatRoughness: 0.15,
    transparent: preset.pbrOptions?.transparent ?? false,
    side: preset.pbrOptions?.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
  });

  const chunks = preset.pbrChunks!;
  const uniforms: Record<string, { value: unknown }> = {};
  // Only animated presets carry a time uniform — its presence is what keeps
  // the render loop drawing while dice sit at rest (collectShaderMaterials
  // keys on it), so static presets must not have one.
  if (chunks.fragmentDecl.includes('uniform float time')) uniforms.time = { value: 0 };
  for (const [key, meta] of Object.entries(preset.uniforms)) {
    if (PBR_PROP_KEYS.has(key)) continue;
    uniforms[key] = { value: meta.type === 'color' ? new THREE.Color(raw[key] as string) : raw[key] };
  }
  // Pattern presets sample the slot's dropped texture (config.map)
  if (preset.usesTexture) {
    const tex = config.map?.url ? loadTexture(config.map.url) : null;
    uniforms.patternMap = { value: tex };
    uniforms.hasPattern = { value: tex ? 1 : 0 };
  }
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', chunks.vertexDecl + '\n#include <common>')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + chunks.vertexMain);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', chunks.fragmentDecl + '\n#include <common>')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n' + chunks.fragmentMain);
  };
  // The default program cache key ignores closure state — without this, all
  // onBeforeCompile materials with identical function source share a program.
  mat.customProgramCacheKey = () => 'sdl-pbr-' + presetName;

  (mat as unknown as { uniforms: typeof uniforms }).uniforms = uniforms;
  mat.userData.pbrShaderPreset = presetName;

  // Material.clone() copies neither instance-level onBeforeCompile /
  // customProgramCacheKey nor the exposed uniforms ref — a clone would be a
  // plain UNPATCHED MeshPhysicalMaterial (the preset silently vanishes).
  // DiceAssetManager.applyMaterials assigns newMat.clone() per mesh, so every
  // in-game die depends on clone; rebuild the full patched material instead.
  mat.clone = (() => buildPbrShader(config, presetName, preset)) as typeof mat.clone;

  return mat;
}

function buildShader(config: MaterialSlotConfig): THREE.Material {
  const presetName = config.shaderPreset ?? 'holographic';
  const preset = SHADER_PRESETS[presetName];
  if (!preset) {
    return new THREE.ShaderMaterial({
      uniforms: { time: { value: 0 } },
      vertexShader: 'void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'void main() { gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); }',
    });
  }

  if (preset.pbr) return buildPbrShader(config, presetName, preset);

  const uniforms: Record<string, { value: any }> = { time: { value: 0 } };

  for (const [key, meta] of Object.entries(preset.uniforms)) {
    const stored = config.shaderUniforms?.[key];
    const rawValue = stored !== undefined ? stored.value : meta.default;

    if (meta.type === 'color') {
      uniforms[key] = { value: new THREE.Color(rawValue as string) };
    } else if (meta.type === 'vec3') {
      const v = rawValue as number[];
      uniforms[key] = { value: new THREE.Vector3(v[0], v[1], v[2]) };
    } else if (meta.type === 'vec2') {
      const v = rawValue as number[];
      uniforms[key] = { value: new THREE.Vector2(v[0], v[1]) };
    } else {
      uniforms[key] = { value: rawValue };
    }
  }

  return new THREE.ShaderMaterial({
    uniforms,
    vertexShader: preset.vertexShader,
    fragmentShader: preset.fragmentShader,
    transparent: true,
    side: THREE.DoubleSide,
  });
}

/** Build a Three.js material from a config object */
export function buildMaterial(config: MaterialSlotConfig): THREE.Material {
  switch (config.type) {
    case 'basic': return buildBasic(config);
    case 'lambert': return buildLambert(config);
    case 'phong': return buildPhong(config);
    case 'standard': return buildStandard(config);
    case 'physical': return buildPhysical(config);
    case 'shader': return buildShader(config);
    default: return buildStandard(config);
  }
}

/** Dispose a material (textures are managed by the shared cache) */
export function disposeMaterial(material: THREE.Material): void {
  material.dispose();
}

/** Clear the texture cache */
export function clearTextureCache(): void {
  for (const tex of textureCache.values()) tex.dispose();
  textureCache.clear();
}
